/**
 * Iter 55 — Accept Invitation page.
 *
 * Public route (no auth). User opens /accept-invite?token=<x>, the page
 * fetches /tenant/users/invitations/{token}, then renders a password
 * setup form. On accept the user is redirected to /login.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import LegalFooter from "@/components/legal/LegalFooter";
import AuthAcknowledgement from "@/components/legal/AuthAcknowledgement";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [state, setState] = useState({ loading: true, inv: null, error: null });
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, inv: null, error: "Missing token" }); return; }
    let cancelled = false;
    api.get(`/tenant/users/invitations/${token}`)
      .then((r) => { if (!cancelled) setState({ loading: false, inv: r.data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, inv: null, error: e?.response?.data?.detail || e.message }); });
    return () => { cancelled = true; };
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    if (pwd.length < 8) {
      setSubmitError("Password must be at least 8 characters");
      return;
    }
    if (pwd !== confirm) {
      setSubmitError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite(token, pwd);
      setDone(true);
      // Founder Commercial Amendment (2026-09-07) -- a self-service
      // Starter signup lands here too (same accept-invite flow). If the
      // tenant hasn't paid yet, skip the workspace entirely and drop
      // the new owner straight into Checkout for the plan they picked
      // at signup, instead of showing them an empty, blocked portfolio.
      let dest = "/portfolio";
      try {
        const { data: billing } = await api.get("/billing/status");
        if (!billing.has_active_subscription && billing.pending_lookup_key) {
          dest = `/settings?autocheckout=${billing.pending_lookup_key}${billing.pending_checkout_trial ? "&trial=1" : ""}`;
        }
      } catch { /* fall through to /portfolio if this check fails */ }
      setTimeout(() => navigate(dest), 900);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (state.loading) {
    return (
      <Shell><Loader2 className="w-6 h-6 animate-spin text-[var(--fop-text-2)]" /></Shell>
    );
  }

  if (state.error || !state.inv) {
    return (
      <Shell>
        <div data-testid="invite-error" className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <div className="text-base font-medium text-[var(--fop-text)]">Invitation unavailable</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">{state.error || "This link is invalid or expired."}</div>
        </div>
      </Shell>
    );
  }

  const inv = state.inv;
  if (inv.status === "expired") {
    return (
      <Shell>
        <div data-testid="invite-expired" className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <div className="text-base font-medium">Invitation expired</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">Ask your admin to resend the invitation.</div>
        </div>
      </Shell>
    );
  }
  if (inv.status === "accepted" || inv.status === "revoked") {
    const isAccepted = inv.status === "accepted";
    return (
      <Shell>
        <div data-testid="invite-used" className="text-center">
          <CheckCircle2 className={`w-10 h-10 mx-auto mb-3 ${isAccepted ? "text-emerald-400" : "text-amber-400"}`} />
          <div className="text-base font-medium">{isAccepted ? "This invitation has already been activated" : "Invitation revoked"}</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            {isAccepted ? "Your account is ready — sign in with the password you set." : "Ask your admin to resend the invitation."}
          </div>
          {isAccepted && (
            <Link to="/login" data-testid="invite-signin-cta" className="inline-block mt-4 px-4 py-2 bg-[var(--fop-primary)] text-black rounded text-sm font-mono uppercase tracking-wider hover:opacity-90">
              Sign in
            </Link>
          )}
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div data-testid="invite-done" className="text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <div className="text-base font-medium">Account activated</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">Taking you to your workspace…</div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell branding={inv.branding}>
      <div className="text-xs uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">
        Join {inv.tenant_display_name}
      </div>
      <h1 className="text-2xl font-semibold text-[var(--fop-text)] mb-1">Set your password</h1>
      <div className="text-sm text-[var(--fop-text-2)] mb-6">
        <span className="font-mono">{inv.email}</span> · {inv.role_label}
      </div>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form">
        <div>
          <Label htmlFor="pwd" className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Password</Label>
          <Input id="pwd" type="password" value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="invite-password"
                  className="mt-1 bg-[var(--fop-surface-2)] border-[var(--fop-border)]" />
        </div>
        <div>
          <Label htmlFor="conf" className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Confirm password</Label>
          <Input id="conf" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="invite-confirm"
                  className="mt-1 bg-[var(--fop-surface-2)] border-[var(--fop-border)]" />
        </div>
        {submitError && (
          <div data-testid="invite-error-msg" className="text-xs text-red-400">{submitError}</div>
        )}
        <Button type="submit" disabled={submitting}
                 data-testid="invite-submit"
                 className="w-full bg-[var(--fop-primary)] hover:opacity-90">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate Account"}
        </Button>
      </form>
      <AuthAcknowledgement testid="invite-acknowledgement" action="setting a password for" />
    </Shell>
  );
}


function Shell({ children, branding }) {
  const bg = "bg-[var(--fop-bg)]";
  return (
    <div className={`min-h-screen ${bg} flex flex-col`}>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-8">
          {branding?.logo_url && (
            <img src={branding.logo_url} alt="logo" className="h-8 mb-6" />
          )}
          {children}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
